import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { SeatStatus } from "@/lib/seat-utils";

interface SeatProps {
  seatNumber: string;
  status: SeatStatus;
  isOwnHold?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * Editorial seat button.
 * Sharp corners, hairline borders, tokens instead of green-100/yellow-200.
 * Selected state is ink-filled, brand-ringed.
 */
export function Seat({
  seatNumber,
  status,
  isOwnHold,
  onClick,
  disabled,
}: SeatProps) {
  const { t } = useTranslation();
  const isClickable = status === "available" || status === "selected" || isOwnHold;
  const isDisabled = disabled || !isClickable;

  const statusClasses = (() => {
    switch (status) {
      case "occupied":
        return "bg-stone-100 text-ink-subtle border-hairline cursor-not-allowed";
      case "held":
        return isOwnHold
          ? "bg-brand text-brand-ink border-brand cursor-pointer"
          : "bg-stone-200 text-ink-muted border-hairline-strong cursor-not-allowed";
      case "selected":
        return "bg-ink text-stone-50 border-ink shadow-[0_0_0_2px_hsl(var(--brand))_inset]";
      default:
        return "bg-white text-ink border-hairline-strong hover:border-ink hover:bg-stone-50";
    }
  })();

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={cn(
        "w-10 h-11 sm:w-11 sm:h-12 border text-[0.85rem] font-medium tabular",
        "flex items-center justify-center transition-colors duration-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1",
        "touch-manipulation rounded-sm",
        statusClasses,
        isClickable && !disabled && "cursor-pointer active:scale-[0.96]",
        isDisabled && "cursor-not-allowed",
      )}
      aria-label={t("a11y.seatStatus", {
        seat: seatNumber,
        status: t(`seats.legend.${status}`),
      })}
      aria-pressed={status === "selected"}
      data-testid={`seat-${seatNumber}`}
      data-seat-status={status}
    >
      {seatNumber}
    </button>
  );
}
